import { c as _c } from "react/compiler-runtime";
import { postHostedUserChallenge, postHostedUserChallengeVerify } from '@onefootprint/fetch-hosted';
import type { ActionKind, ChallengeKind } from '@onefootprint/request-types';
import { Form, LoadingSpinner, PageShell, SuccessCheck, useToast } from '@onefootprint/ui';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ResendButton } from '@/idv2/features/identify/resend-button';
import { getLogger } from '@/idv2/utils/logger';
const {
  logWarn
} = getLogger({
  name: 'idv2',
  location: 'auth-methods-verify'
});
const SUCCESS_DELAY_MS = 1500;
type UpdateVerifyProps = {
  token: string;
  challengeKind: ChallengeKind;
  actionKind: ActionKind;
  contactValue: string;
  title: string;
  onSuccess: () => void;
};
type OtpFormData = {
  otp: string;
};
type ChallengeState = {
  challengeToken: string;
  retryDisabledUntil?: Date;
};
export const UpdateVerify = t0 => {
  const $ = _c(81);
  const {
    token,
    challengeKind,
    actionKind,
    contactValue,
    title,
    onSuccess
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "authMethods.verify"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("idv2", t1);
  const {
    toast
  } = useToast();
  const [challengeState, setChallengeState] = useState();
  const hasInitiated = useRef(false);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      defaultValues: {
        otp: ""
      }
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const methods = useForm(t2);
  let t3;
  if ($[2] !== actionKind || $[3] !== challengeKind || $[4] !== contactValue || $[5] !== token) {
    t3 = async () => {
      const payload = {
        kind: challengeKind,
        actionKind
      };
      if (challengeKind === "email") {
        payload.email = contactValue;
      }
      if (challengeKind === "sms") {
        payload.phoneNumber = contactValue;
      }
      const {
        data
      } = await postHostedUserChallenge({
        headers: {
          "X-Fp-Authorization": token
        },
        body: payload as Parameters<typeof postHostedUserChallenge>[0]['body'],
        throwOnError: true
      });
      return data;
    };
    $[2] = actionKind;
    $[3] = challengeKind;
    $[4] = contactValue;
    $[5] = token;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = data_0 => {
      setChallengeState({
        challengeToken: data_0.challengeToken,
        retryDisabledUntil: data_0.timeBeforeRetryS ? new Date(Date.now() + data_0.timeBeforeRetryS * 1000) : undefined
      });
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== toast) {
    t5 = err => {
      logWarn("Failed to initiate challenge", err);
      toast({
        title: "Error",
        description: "Failed to send verification code",
        variant: "error"
      });
    };
    $[8] = toast;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t3 || $[11] !== t5) {
    t6 = {
      mutationFn: t3,
      onSuccess: t4,
      onError: t5
    };
    $[10] = t3;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const challengeMutation = useMutation(t6);
  let t7;
  if ($[13] !== challengeState || $[14] !== token) {
    t7 = async challengeResponse => {
      if (!challengeState) {
        throw new Error("No challenge state");
      }
      await postHostedUserChallengeVerify({
        headers: {
          "X-Fp-Authorization": token
        },
        body: {
          challengeToken: challengeState.challengeToken,
          challengeResponse
        },
        throwOnError: true
      });
    };
    $[13] = challengeState;
    $[14] = token;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== methods || $[17] !== t) {
    t8 = err_0 => {
      logWarn("Failed to verify challenge", err_0);
      methods.setError("otp", {
        message: t(_temp)
      });
      methods.setValue("otp", "");
    };
    $[16] = methods;
    $[17] = t;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== challengeKind || $[20] !== onSuccess || $[21] !== t || $[22] !== toast) {
    t9 = () => {
      const successMessage = challengeKind === "email" ? t(_temp2) : t(_temp3);
      toast({
        title: t(_temp4),
        description: successMessage
      });
      setTimeout(onSuccess, SUCCESS_DELAY_MS);
    };
    $[19] = challengeKind;
    $[20] = onSuccess;
    $[21] = t;
    $[22] = toast;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t7 || $[25] !== t8 || $[26] !== t9) {
    t10 = {
      mutationFn: t7,
      onError: t8,
      onSuccess: t9
    };
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const verifyMutation = useMutation(t10);
  let t11;
  if ($[28] !== challengeMutation) {
    t11 = () => {
      if (!hasInitiated.current) {
        hasInitiated.current = true;
        challengeMutation.mutate();
      }
    };
    $[28] = challengeMutation;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = [];
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  useEffect(t11, t12);
  let t13;
  if ($[31] !== verifyMutation) {
    t13 = value => {
      if (verifyMutation.isPending) {
        return;
      }
      verifyMutation.mutate(value);
    };
    $[31] = verifyMutation;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  const handleComplete = t13;
  let t14;
  if ($[33] !== challengeMutation || $[34] !== challengeState) {
    t14 = () => {
      if (challengeState?.retryDisabledUntil && challengeState.retryDisabledUntil > new Date()) {
        return;
      }
      challengeMutation.mutate();
    };
    $[33] = challengeMutation;
    $[34] = challengeState;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  const handleResend = t14;
  let t15;
  if ($[36] !== challengeKind || $[37] !== contactValue || $[38] !== t) {
    t15 = challengeKind === "email" ? t(_temp5, {
      email: contactValue
    }) : t(_temp6, {
      phone: contactValue
    });
    $[36] = challengeKind;
    $[37] = contactValue;
    $[38] = t;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  const subtitle = t15;
  const isLoading = challengeMutation.isPending && !challengeState;
  if (verifyMutation.isSuccess) {
    let t16;
    if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = <SuccessCheck animationStart={true} size={32} />;
      $[40] = t16;
    } else {
      t16 = $[40];
    }
    let t17;
    if ($[41] !== t) {
      t17 = t(_temp7);
      $[41] = t;
      $[42] = t17;
    } else {
      t17 = $[42];
    }
    let t18;
    if ($[43] !== t17) {
      t18 = <div className="flex flex-col justify-center items-center h-[100px] gap-3">{t16}<p className="text-label-2 text-success">{t17}</p></div>;
      $[43] = t17;
      $[44] = t18;
    } else {
      t18 = $[44];
    }
    let t19;
    if ($[45] !== subtitle || $[46] !== t18 || $[47] !== title) {
      t19 = <PageShell title={title} subtitle={subtitle}>{t18}</PageShell>;
      $[45] = subtitle;
      $[46] = t18;
      $[47] = title;
      $[48] = t19;
    } else {
      t19 = $[48];
    }
    return t19;
  }
  if (verifyMutation.isPending) {
    let t16;
    if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = <div className="flex justify-center items-center h-8"><LoadingSpinner /></div>;
      $[49] = t16;
    } else {
      t16 = $[49];
    }
    let t17;
    if ($[50] !== t) {
      t17 = t(_temp8);
      $[50] = t;
      $[51] = t17;
    } else {
      t17 = $[51];
    }
    let t18;
    if ($[52] !== t17) {
      t18 = <div className="flex flex-col justify-center items-center h-[100px] gap-3">{t16}<p className="text-label-2 text-primary">{t17}</p></div>;
      $[52] = t17;
      $[53] = t18;
    } else {
      t18 = $[53];
    }
    let t19;
    if ($[54] !== subtitle || $[55] !== t18 || $[56] !== title) {
      t19 = <PageShell title={title} subtitle={subtitle}>{t18}</PageShell>;
      $[54] = subtitle;
      $[55] = t18;
      $[56] = title;
      $[57] = t19;
    } else {
      t19 = $[57];
    }
    return t19;
  }
  let t16;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      required: true
    };
    $[58] = t16;
  } else {
    t16 = $[58];
  }
  const t17 = !challengeState;
  let t18;
  if ($[59] !== handleComplete || $[60] !== t17) {
    t18 = <Form.Otp name="otp" rules={t16} disabled={t17} onComplete={handleComplete} autoFocus={true} />;
    $[59] = handleComplete;
    $[60] = t17;
    $[61] = t18;
  } else {
    t18 = $[61];
  }
  let t19;
  if ($[62] !== isLoading) {
    t19 = isLoading && <LoadingSpinner className="min-h-[20px]" />;
    $[62] = isLoading;
    $[63] = t19;
  } else {
    t19 = $[63];
  }
  let t20;
  if ($[64] !== t18 || $[65] !== t19) {
    t20 = <div className="w-full flex flex-col items-center gap-2">{t18}{t19}</div>;
    $[64] = t18;
    $[65] = t19;
    $[66] = t20;
  } else {
    t20 = $[66];
  }
  const t21 = challengeState?.retryDisabledUntil;
  let t22;
  if ($[67] !== challengeMutation.isPending || $[68] !== handleResend || $[69] !== t21) {
    t22 = <ResendButton onResend={handleResend} isResendLoading={challengeMutation.isPending} resendDisabledUntil={t21} showLegalFooter={false} />;
    $[67] = challengeMutation.isPending;
    $[68] = handleResend;
    $[69] = t21;
    $[70] = t22;
  } else {
    t22 = $[70];
  }
  let t23;
  if ($[71] !== t20 || $[72] !== t22) {
    t23 = <div className="flex flex-col gap-6 items-center w-full">{t20}{t22}</div>;
    $[71] = t20;
    $[72] = t22;
    $[73] = t23;
  } else {
    t23 = $[73];
  }
  let t24;
  if ($[74] !== methods || $[75] !== t23) {
    t24 = <FormProvider {...methods}>{t23}</FormProvider>;
    $[74] = methods;
    $[75] = t23;
    $[76] = t24;
  } else {
    t24 = $[76];
  }
  let t25;
  if ($[77] !== subtitle || $[78] !== t24 || $[79] !== title) {
    t25 = <PageShell title={title} subtitle={subtitle}>{t24}</PageShell>;
    $[77] = subtitle;
    $[78] = t24;
    $[79] = title;
    $[80] = t25;
  } else {
    t25 = $[80];
  }
  return t25;
};
function _temp($) {
  return $.incorrectCode;
}
function _temp2($_0) {
  return $_0.emailUpdateSuccess;
}
function _temp3($_1) {
  return $_1.phoneUpdateSuccess;
}
function _temp4($_2) {
  return $_2.success;
}
function _temp5($_3) {
  return $_3.emailSubtitle;
}
function _temp6($_4) {
  return $_4.phoneSubtitle;
}
function _temp7($_5) {
  return $_5.success;
}
function _temp8($_6) {
  return $_6.verifying;
}